// src/pages/MyPage.tsx
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { authFetch, getToken, openAuthenticatedUrl } from "../lib/auth";
import { toKorStatus } from "../lib/paymentStatus";
import { displayServiceName } from "../lib/serviceLabel";
import "../styles/account-mobile.css";

type Section = "profile" | "password" | "history" | "alerts" | "terms";

type PaymentHistoryItem = {
  id: number;
  created_at: string;

  /** 원금(서버 기본 필드) */
  amount: number;

  status?: string | null;
  title?: string;

  // 서버가 내려줄 수 있는 추가 필드
  approval_status?: string | null;
  order_id?: string | null;

  // ✅ 분해 금액(서버가 내려주면 사용 / 없으면 amount 기반 계산)
  principal_amount?: number | null;
  fee_amount?: number | null;
  vat_amount?: number | null;
  total_amount?: number | null;
};

type NotificationSettings = {
  notice_enabled: boolean;
  payment_enabled: boolean;
  updated_at?: string;
};

export default function MyPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [section, setSection] = useState<Section>("history");

  const [payments, setPayments] = useState<PaymentHistoryItem[] | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);

  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [pwMessage, setPwMessage] = useState<string | null>(null);

  const [alertNotice, setAlertNotice] = useState(true);
  const [alertPayment, setAlertPayment] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [savingAlerts, setSavingAlerts] = useState(false);

  const [termsTab, setTermsTab] = useState<"service" | "privacy">("service");

  // --- 결제 이용내역 불러오기 ---
  useEffect(() => {
    if (section !== "history") return;

    const fetchPayments = async () => {
      try {
        setLoadingPayments(true);
        const res = await authFetch("/api/payments/my");
        if (!res.ok) {
          setPayments([]);
          return;
        }
        const data = await res.json().catch(() => null);
        const items = Array.isArray(data?.items)
          ? (data.items as PaymentHistoryItem[])
          : Array.isArray(data)
          ? (data as PaymentHistoryItem[])
          : [];
        setPayments(items);
      } catch (e) {
        console.error("payments load error", e);
        setPayments([]);
      } finally {
        setLoadingPayments(false);
      }
    };

    fetchPayments();
  }, [section]);

  // --- 알림 설정 불러오기 ---
  useEffect(() => {
    const loadAlerts = async () => {
      try {
        setLoadingAlerts(true);
        const res = await authFetch("/api/me/notifications");
        if (!res.ok) return;

        const data = (await res.json().catch(() => null)) as
          | { ok?: boolean; settings?: NotificationSettings | null }
          | null;

        const s = data?.settings;
        if (s && typeof s.notice_enabled === "boolean") setAlertNotice(s.notice_enabled);
        if (s && typeof s.payment_enabled === "boolean") setAlertPayment(s.payment_enabled);
      } catch (e) {
        console.warn("alert load error", e);
      } finally {
        setLoadingAlerts(false);
      }
    };

    loadAlerts();
  }, []);

  const handleLogout = async () => {
    if (!window.confirm("로그아웃 하시겠습니까?")) return;
    await logout();
    navigate("/");
  };

  const handleWithdraw = async () => {
    if (
      !window.confirm(
        "정말 회원탈퇴 하시겠습니까?\n탈퇴 후에는 결제 내역 등 정보가 삭제될 수 있습니다."
      )
    ) {
      return;
    }
    try {
      const res = await authFetch("/api/me/withdraw", { method: "POST" });
      if (!res.ok) {
        alert("탈퇴 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.");
        return;
      }
      alert("회원탈퇴가 완료되었습니다.");
      await logout();
      navigate("/");
    } catch (e) {
      console.error(e);
      alert("탈퇴 처리 중 오류가 발생했습니다.");
    }
  };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    setPwMessage(null);

    if (!oldPw || !newPw || !newPw2) {
      setPwMessage("모든 항목을 입력해 주세요.");
      return;
    }
    if (newPw !== newPw2) {
      setPwMessage("변경하실 비밀번호와 확인이 일치하지 않습니다.");
      return;
    }
    if (newPw.length < 8) {
      setPwMessage("비밀번호는 8자 이상으로 설정해 주세요.");
      return;
    }

    try {
      const res = await authFetch("/api/my/password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          currentPassword: oldPw,
          newPassword: newPw,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || (data && data.ok === false)) {
        setPwMessage(data?.message || "비밀번호 변경에 실패했습니다.");
        return;
      }

      setOldPw("");
      setNewPw("");
      setNewPw2("");
      setPwMessage("비밀번호가 변경되었습니다.");
    } catch (e) {
      console.error(e);
      setPwMessage("비밀번호 변경 중 오류가 발생했습니다.");
    }
  };

  // --- 알림 설정 저장 ---
  const handleSaveAlerts = async () => {
    try {
      setSavingAlerts(true);

      const res = await authFetch("/api/me/notifications", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          notice_enabled: alertNotice,
          payment_enabled: alertPayment,
        }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok || data?.ok === false) {
        alert(data?.message || "알림 설정 저장에 실패했습니다.");
        return;
      }

      alert("알림 설정이 저장되었습니다.");
    } catch (e) {
      console.error(e);
      alert("알림 설정 저장 중 오류가 발생했습니다.");
    } finally {
      setSavingAlerts(false);
    }
  };

  const displayName = (user as any)?.name || "회원님";
  const email = (user as any)?.email || "";
  const phone = (user as any)?.phone || "";

  return (
    <div className="jp-account-page">
      <div className="jp-account-wrap">
        <header className="jp-account-heading">
          <h1>마이페이지</h1>
          <p>내 정보와 이용내역을 확인하세요.</p>
        </header>
        <div className="jp-account-layout">
          {/* 왼쪽 사이드바 */}
          <aside className="jp-account-sidebar">
            <div className="jp-account-identity">
              <div className="jp-account-avatar" aria-hidden="true">
                {String(displayName).charAt(0)}
              </div>
              <strong>{displayName}</strong>
              <span>{email}</span>
            </div>

            <nav className="jp-account-tabs" aria-label="마이페이지 메뉴">
              <SideItem active={section === "profile"} onClick={() => setSection("profile")} label="내 정보" />
              <SideItem active={section === "password"} onClick={() => setSection("password")} label="비밀번호 변경" />
              <SideItem active={section === "history"} onClick={() => setSection("history")} label="결제 이용내역" />
              <SideItem active={section === "alerts"} onClick={() => setSection("alerts")} label="알림 설정" />
              <SideItem active={section === "terms"} onClick={() => setSection("terms")} label="약관 안내" />

              <div className="jp-account-exit">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="jp-account-logout"
                >
                  <span>로그아웃</span>
                </button>
                <button
                  type="button"
                  onClick={handleWithdraw}
                  className="jp-account-withdraw"
                >
                  <span>회원탈퇴</span>
                </button>
              </div>
            </nav>
          </aside>

          {/* 오른쪽 메인 영역 */}
          <section id="jp-account-section" className="jp-account-content" aria-label="계정 정보">
            {section === "profile" && <ProfileSection name={displayName} email={email} phone={phone} />}
            {section === "password" && (
              <PasswordSection
                oldPw={oldPw}
                newPw={newPw}
                newPw2={newPw2}
                onOldPw={setOldPw}
                onNewPw={setNewPw}
                onNewPw2={setNewPw2}
                onSubmit={handlePasswordChange}
                message={pwMessage}
              />
            )}
            {section === "history" && <HistorySection payments={payments} loading={loadingPayments} />}
            {section === "alerts" && (
              <AlertsSection
                alertNotice={alertNotice}
                alertPayment={alertPayment}
                setAlertNotice={setAlertNotice}
                setAlertPayment={setAlertPayment}
                onSave={handleSaveAlerts}
                saving={savingAlerts}
                loading={loadingAlerts}
              />
            )}
            {section === "terms" && <TermsSection termsTab={termsTab} setTermsTab={setTermsTab} />}
          </section>
        </div>
      </div>
    </div>
  );
}

// ---------------- Sub components ----------------

function SideItem({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="jp-account-tab"
      aria-current={active ? "page" : undefined}
      aria-controls="jp-account-section"
    >
      <span>{label}</span>
      <span aria-hidden="true">›</span>
    </button>
  );
}

function ProfileSection({
  name,
  email,
  phone,
}: {
  name: string;
  email?: string;
  phone?: string;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h2>내 정보</h2>
        <p className="jp-account-description">정보 변경은 고객센터로 문의해 주세요.</p>
      </div>
      <dl className="jp-account-details">
        <ItemRow label="이름" value={name} />
        <ItemRow label="이메일" value={email || "-"} />
        <ItemRow label="휴대폰 번호" value={phone || "-"} />
      </dl>
    </div>
  );
}

function ItemRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="jp-account-detail">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
function PasswordSection(props: {
  oldPw: string;
  newPw: string;
  newPw2: string;
  onOldPw: (v: string) => void;
  onNewPw: (v: string) => void;
  onNewPw2: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
  message: string | null;
}) {
  const { oldPw, newPw, newPw2, onOldPw, onNewPw, onNewPw2, onSubmit, message } = props;

  const isOk =
    oldPw.trim().length > 0 && newPw.trim().length >= 8 && newPw2.trim().length > 0;

  return (
    <form className="jp-account-password space-y-6" onSubmit={onSubmit}>
      <h2 className="text-lg font-semibold text-slate-900">비밀번호 변경</h2>

      <div className="space-y-4 text-sm">
        <div>
          <label htmlFor="account-current-password" className="mb-1 block text-xs text-slate-600">현재 비밀번호</label>
          <input
            id="account-current-password"
            autoComplete="current-password"
            type="password"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
            placeholder="현재 비밀번호"
            value={oldPw}
            onChange={(e) => onOldPw(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="account-new-password" className="mb-1 block text-xs text-slate-600">새 비밀번호</label>
          <input
            id="account-new-password"
            autoComplete="new-password"
            type="password"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
            placeholder="8자 이상"
            value={newPw}
            onChange={(e) => onNewPw(e.target.value)}
          />
        </div>

        <div>
          <label htmlFor="account-confirm-password" className="mb-1 block text-xs text-slate-600">새 비밀번호 확인</label>
          <input
            id="account-confirm-password"
            autoComplete="new-password"
            type="password"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-orange-400 focus:ring-1 focus:ring-orange-400"
            placeholder="비밀번호 확인"
            value={newPw2}
            onChange={(e) => onNewPw2(e.target.value)}
          />
        </div>
      </div>

      {message && (
        <p
          role="status"
          className={`whitespace-pre-line text-xs ${
            message.includes("변경되었습니다") ? "text-emerald-600" : "text-red-500"
          }`}
        >
          {message}
        </p>
      )}

      <button
        type="submit"
        disabled={!isOk}
        className="jp-account-primary"
      >
        변경하기
      </button>
    </form>
  );
}

/* ===========================
   ✅ 결제 이용내역
   =========================== */

function HistorySection({
  payments,
  loading,
}: {
  payments: PaymentHistoryItem[] | null;
  loading: boolean;
}) {
  const nf = new Intl.NumberFormat("ko-KR");
  const won = (n?: number | null) => `${nf.format(Math.max(0, Number(n ?? 0)))}원`;

  const computeAmounts = (p: PaymentHistoryItem) => {
    const principal = Number(p.principal_amount ?? p.amount ?? 0);
    const fee = Number(p.fee_amount ?? 0);
    const vat = Number(p.vat_amount ?? 0);
    const total = p.total_amount != null ? Number(p.total_amount) : principal + fee + vat;
    return { principal, fee, vat, total };
  };

  const getStatusLabel = (p: PaymentHistoryItem) => {
    const ap = (p.approval_status || "").trim();
    if (ap) return ap;
    return toKorStatus(p.status ?? "");
  };

  const isRemitDone = (p: PaymentHistoryItem) => getStatusLabel(p) === "송금완료";

  const openStatement = (paymentId: number) => {
    const token = getToken();
    const qs = token ? `?token=${encodeURIComponent(token)}` : "";
    const url = `/api/payments/${paymentId}/statement${qs}`;
    void openAuthenticatedUrl(url);
  };

  const shouldHide = (p: PaymentHistoryItem) => {
    const title = String(p.title || "").replace(/\s/g, "");
    const catRaw = String((p as any).category || "").replace(/\s/g, "");
    const cat = catRaw.toLowerCase();

    if (title.includes("등기부등본") || title.includes("등기부")) return true;
    if (cat.includes("등기부") || cat.includes("registry")) return true;

    const { total } = computeAmounts(p);
    if ((title.includes("발급") || title.includes("수수료")) && total === 1200) return true;

    return false;
  };

  const pickPrimaryTitle = (p: PaymentHistoryItem) => {
    const cat = String((p as any).category || "").trim();
    const counterparty = String(
      (p as any).counterparty_name ||
        (p as any).account_holder ||
        (p as any).receiver_name ||
        (p as any).depositor_name ||
        ""
    ).trim();

    // ✅ drink → 주류/음료 표시
    const displayCat = displayServiceName(cat);
    const displayCounterparty = counterparty;

    if (displayCat || displayCounterparty) {
      return [displayCat || null, displayCounterparty || null].filter(Boolean).join(", ");
    }
    
    // ✅ title도 drink 포함 시 변환
    return displayServiceName(p.title) || "이체 결제";
  };

  const visiblePayments = (payments || []).filter((p) => !shouldHide(p));

  if (loading) {
    return (
      <div>
        <h2>결제 이용내역</h2>
        <div className="jp-account-empty" role="status">
          이용내역을 불러오는 중입니다…
        </div>
      </div>
    );
  }

  if (!visiblePayments || visiblePayments.length === 0) {
    return (
      <div>
        <h2>결제 이용내역</h2>
        <div className="jp-account-empty">
          <p>아직 결제 이용내역이 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2>결제 이용내역</h2>

      <div className="jp-account-history">
        <ul>
          {visiblePayments.map((p) => {
            const d = new Date(p.created_at);
            const datePart = Number.isNaN(d.getTime())
              ? "-"
              : d.toLocaleDateString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit" });

            const timePart = Number.isNaN(d.getTime())
              ? ""
              : d.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit", hour12: true });

            const statusLabel = getStatusLabel(p);
            const done = isRemitDone(p);
            const { principal, fee, vat, total } = computeAmounts(p);

            const primaryTitle = pickPrimaryTitle(p);
            const rawSecondary = p.title && primaryTitle !== displayServiceName(p.title) ? p.title : null;
            const secondaryTitle = rawSecondary ? displayServiceName(rawSecondary) : null;

            return (
              <li
                key={p.id}
                className="jp-account-payment"
              >
                <div className="jp-account-payment-heading">
                  <div>
                    <h3>
                      {primaryTitle}
                    </h3>

                    {secondaryTitle && (
                      <p className="jp-account-payment-subtitle">
                        {secondaryTitle}
                      </p>
                    )}

                    <p className="jp-account-payment-date">{datePart} {timePart}</p>
                  </div>

                  <span className={`jp-account-payment-status${done ? " is-complete" : ""}`}>{statusLabel}</span>
                </div>
                <div className="jp-account-payment-total"><span>결제금액</span><strong>{won(total)}</strong></div>
                <dl className="jp-account-payment-breakdown">
                  <div><dt>송금액</dt><dd>{won(principal)}</dd></div>
                  <div><dt>이용료</dt><dd>{won(fee)}</dd></div>
                  <div><dt>부가세</dt><dd>{won(vat)}</dd></div>
                </dl>

                {done && (
                  <div className="jp-account-payment-actions">
                    <button
                      type="button"
                      onClick={() => openStatement(p.id)}
                      className="jp-account-secondary"
                    >
                      거래명세서 보기
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
function AlertsSection(props: {
  alertNotice: boolean;
  alertPayment: boolean;
  setAlertNotice: (v: boolean) => void;
  setAlertPayment: (v: boolean) => void;
  onSave: () => void;
  saving: boolean;
  loading: boolean;
}) {
  const { alertNotice, alertPayment, setAlertNotice, setAlertPayment, onSave, saving, loading } = props;

  const disabled = loading || saving;

  return (
    <div className="space-y-6">
      <h2>알림 설정</h2>

      <div className="space-y-5 text-sm">
        <ToggleRow
          label="공지사항 알림"
          description="새 소식과 혜택을 알려드려요."
          checked={alertNotice}
          onChange={setAlertNotice}
          disabled={disabled}
        />
        <ToggleRow
          label="결제 승인 알림"
          description="승인 여부를 카카오톡으로 알려드려요."
          checked={alertPayment}
          onChange={setAlertPayment}
          disabled={disabled}
        />
      </div>

      <button
        type="button"
        onClick={onSave}
        disabled={disabled}
        className="jp-account-primary"
      >
        {loading ? "불러오는 중…" : saving ? "저장 중…" : "설정 저장"}
      </button>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="jp-account-toggle-row">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="mt-1 text-xs text-slate-500 leading-relaxed">{description}</div>
      </div>

      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={[
          "jp-account-toggle relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition",
          checked ? "bg-orange-500" : "bg-slate-300",
          disabled ? "cursor-not-allowed opacity-60" : "",
        ].join(" ")}
        aria-pressed={checked}
        aria-label={label}
      >
        <span
          className={[
            "inline-block h-5 w-5 transform rounded-full bg-white transition",
            checked ? "translate-x-6" : "translate-x-1",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

// ------- 약관 섹션 -------

function TermsSection({
  termsTab,
  setTermsTab,
}: {
  termsTab: "service" | "privacy";
  setTermsTab: (v: "service" | "privacy") => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <h2>약관 안내</h2>

      <div className="jp-account-terms-tabs">
        <button
          type="button"
          onClick={() => setTermsTab("service")}
          aria-pressed={termsTab === "service"}
        >
          서비스 이용약관
        </button>
        <button
          type="button"
          onClick={() => setTermsTab("privacy")}
          aria-pressed={termsTab === "privacy"}
        >
          개인정보 처리방침
        </button>
      </div>

      <div className="jp-account-terms-body" tabIndex={0} aria-label={termsTab === "service" ? "서비스 이용약관 내용" : "개인정보 처리방침 내용"}>
        {termsTab === "service" ? <ServiceTerms /> : <PrivacyTerms />}
      </div>
    </div>
  );
}

// 아래 약관 컴포넌트는 네가 준 그대로 유지
function ServiceTerms() {
  return (
    <div className="space-y-4 whitespace-pre-line">
      {`제1조 (목적)
이 약관(이하 "약관"이라 합니다)은 주식회사 주류페이(이하 "회사"라 합니다)가 제공하는 결제 대행 서비스(이하 "서비스"라 합니다)의 이용과 관련하여 회사와 회원 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.

제2조 (정의)
이 약관에서 사용하는 용어의 정의는 다음과 같습니다.

**"서비스"**란 회원이 현금이 없거나 결제 수단이 없는 경우 회사가 제공하는 결제 대행 서비스를 의미합니다.
**"회원"**이란 서비스에 가입하여 회사가 제공하는 서비스를 이용하는 개인 또는 법인을 말합니다.
**"결제수단"**이란 회원이 서비스 이용 시 사용하는 신용카드 등 전자 결제수단을 의미합니다.

제3조 (약관의 게시와 개정)
회사는 약관의 내용을 회원이 쉽게 알 수 있도록 홈페이지나 모바일 앱을 통해 게시합니다.
회사는 필요 시 관련 법령을 위배하지 않는 범위 내에서 약관을 개정할 수 있습니다.
회사가 약관을 개정할 경우 개정 내용과 적용 일자를 명시하여 적용일자 7일 전부터 서비스 내 게시판이나 홈페이지를 통해 공지합니다. 단, 회원에게 불리하게 개정되는 경우에는 적용일자 30일 전부터 공지합니다.
회원은 개정된 약관에 대해 동의하지 않을 권리가 있으며, 개정된 약관에 동의하지 않는 경우 회원 탈퇴를 요청할 수 있습니다. 개정된 약관에 동의하지 않는 회원은 탈퇴할 수 있으며, 탈퇴하지 않고 서비스를 계속 이용할 경우 변경된 약관에 동의한 것으로 간주됩니다.

제4조 (서비스의 제공 및 변경)
회사는 다음과 같은 서비스를 제공합니다.
- 결제 대행 서비스 제공
- 계약서 등의 계약 증빙 자료 관리
- 결제 내역 및 거래 정보 확인

회사는 서비스의 기술적 변경이나 운영상 필요에 따라 제공 내용을 변경할 수 있으며, 변경 시 이를 사전에 공지합니다.
서비스 제공 내용의 변경으로 인한 회원의 손해에 대해 회사는 고의 또는 중대한 과실이 없는 한 책임을 지지 않습니다.

제5조 (서비스 이용 요금)
서비스 이용에 필요한 수수료는 회원이 부담합니다.
회사는 수수료와 관련된 세부 사항을 서비스 내에서 별도로 공지합니다.

제6조 (서비스 이용 제한)
회사는 다음 각 호에 해당하는 경우 서비스 이용을 제한할 수 있습니다.
- 회원이 타인의 명의나 결제수단을 부정하게 사용한 경우
- 법령 위반, 범죄 행위와 관련된 경우
- 회원이 부당하게 서비스를 이용하여 회사 또는 타인에게 피해를 준 경우
- 기타 회사가 합리적으로 판단할 때 서비스 제공을 중단할 필요가 있는 경우

제7조 (회원의 의무)
회원은 관련 법령 및 본 약관을 준수해야 하며, 타인의 권리나 명예를 침해하는 행위를 해서는 안 됩니다.
회원은 서비스 이용을 위해 제공된 개인정보가 변경된 경우 지체 없이 회사에 알리고, 정보가 정확하지 않거나 누락된 경우 발생하는 문제에 대한 책임은 회원에게 있습니다.

제8조 (회사의 의무)
회사는 회원이 안정적으로 서비스를 이용할 수 있도록 최선을 다합니다.
회사는 서비스 제공과 관련하여 알게 된 회원의 개인정보를 본인의 동의 없이 제3자에게 제공하거나 누설하지 않습니다.

제9조 (손해배상)
회사가 본 약관을 위반하거나 서비스 제공에 과실이 있는 경우, 회사는 법령에 따라 책임을 집니다. 다만, 회사의 고의 또는 중대한 과실이 없는 한 서비스 제공 중 발생한 손해에 대해서는 책임을 지지 않습니다.

제10조 (면책조항)
회사는 천재지변, 전쟁, 폭동, 정부의 규제 등 불가항력적 사유로 인해 서비스를 제공할 수 없는 경우, 이에 대한 책임을 지지 않습니다.
회사는 회원의 귀책사유로 인한 서비스 이용의 장애에 대해서는 책임을 지지 않습니다.
회사는 회원이 서비스와 관련하여 제공한 정보의 오류나 부정확성으로 인한 책임을 지지 않습니다.

제11조 (분쟁 해결 및 관할 법원)
서비스 이용과 관련하여 회사와 회원 간에 발생한 분쟁은 성실히 협의하여 해결합니다.
협의가 이루어지지 않는 경우, 회사의 본사 소재지 관할 법원을 전속 관할 법원으로 합니다.

부칙
본 약관은 2024년 10월 1일부터 적용됩니다.`}
    </div>
  );
}

function PrivacyTerms() {
  return (
    <div className="space-y-4 whitespace-pre-line">
      {`제1조 (개인정보의 처리 목적)
주류페이(이하 "회사"라 합니다)는 아래의 목적을 위해 개인정보를 처리합니다. 처리된 개인정보는 다음의 목적 이외의 용도로는 사용되지 않으며, 이용 목적이 변경될 시에는 사전 동의를 구할 예정입니다.

회원 가입 및 관리: 회원 가입 의사 확인, 회원제 서비스 제공에 따른 본인 식별·인증, 회원 자격 유지·관리, 서비스 부정 이용 방지 등을 목적으로 개인정보를 처리합니다.
서비스 제공: 결제 대행 서비스 제공, 콘텐츠 제공, 맞춤 서비스 제공, 본인 인증, 대금 결제 및 정산 등을 목적으로 개인정보를 처리합니다.
고객 문의 및 불만 처리: 고객의 문의 사항에 대한 응대 및 불만 처리를 목적으로 개인정보를 처리합니다.

제2조 (처리하는 개인정보 항목)
회사는 아래와 같은 개인정보 항목을 수집·처리합니다.

가. 회원 가입 시 수집하는 개인정보 항목
- 회원 이메일, 비밀번호, 이름, 휴대폰 번호

나. 서비스 이용 과정에서 수집하는 개인정보 항목
- 주소, 계약상의 입금계좌 정보(은행명/계좌번호/예금주), 입금자명
- 계약/거래를 입증할 수 있는 증빙 자료: 계약서 등

다. 서비스 이용 중 자동으로 생성되는 정보
- 결제 서비스 이용 중 생성되는 정보: IP 주소, 쿠키, 방문일시, 서비스 이용 기록, 불량 이용 기록, 기기 정보
- 고객 상담 중 생성되는 정보

라. 회사는 이용자의 금융거래 및 신용카드 부정사용 발생 우려가 있는 민감한 금융정보는 어떤 경로로도 수집하지 않습니다.

제3조 (개인정보의 처리 및 보유 기간)
회사는 개인정보 수집·이용 목적이 달성된 후에는 지체 없이 해당 정보를 파기합니다. 다만, 법령에 따라 일정 기간 보관해야 하는 경우에는 해당 기간 동안 안전하게 보관됩니다.

- 회원 가입 및 관리: 회원 탈퇴 시까지
- 대금 결제 및 서비스 제공: 서비스 제공 완료 후 5년
- 고객 문의 및 불만 처리: 문의 처리 후 3년

제4조 (개인정보의 제3자 제공)
회사는 원칙적으로 회원의 개인정보를 외부에 제공하지 않으나, 다음과 같은 경우 예외적으로 제공할 수 있습니다.
- 법령의 규정에 의하거나, 수사기관의 요구가 있는 경우
- 회원의 사전 동의를 받은 경우

제5조 (개인정보 처리의 위탁)
회사는 원활한 서비스 제공을 위해 필요한 경우, 개인정보 처리를 제3자에게 위탁할 수 있습니다. 이 경우 수탁자 및 위탁 업무 내용은 홈페이지에 공지합니다.

제6조 (정보주체의 권리·의무 및 행사 방법)
정보주체는 언제든지 다음과 같은 권리를 행사할 수 있습니다.
- 개인정보 열람 요구
- 오류 등이 있을 경우 정정 요구
- 삭제 요구
- 처리 정지 요구

제7조 (개인정보의 파기 절차 및 방법)
회사는 개인정보 보유 기간이 경과하거나 처리 목적이 달성된 경우, 해당 개인정보를 지체 없이 파기합니다. 파기의 절차 및 방법은 다음과 같습니다.
- 전자적 파일: 복구할 수 없는 기술적 방법으로 파기
- 서면: 분쇄기로 분쇄하거나 소각

제8조 (개인정보 보호책임자 및 문의처)
회사는 개인정보 처리와 관련한 문의 사항을 처리하기 위해 다음과 같은 부서를 지정하고 있습니다.

- 개인정보 보호책임자: 정영호
- 이메일: julyupay@gmail.com
- 전화번호: 063-917-5215

제9조 (개인정보 처리방침의 변경)
본 개인정보 처리방침은 2024년 10월 1일부터 적용되며, 법령이나 서비스 내용의 변경에 따라 방침이 변경될 수 있습니다. 변경이 있을 경우 최소30일 전 홈페이지를 통해 사전 공지합니다.`}
    </div>
  );
}
